import { useState, useEffect } from 'react'
import SeoHead from '../components/SeoHead'
import ContactForm from '../components/ContactForm'
import { getCmsPage } from '../lib/cms'

export default function ContactPage() {
  const [pageData, setPageData] = useState(null)

  useEffect(() => {
    let isMounted = true
    getCmsPage('kontakt').then((page) => {
      if (isMounted) setPageData(page)
    })
    return () => {
      isMounted = false
    }
  }, [])

  return (
    <div className="bg-[#121214] text-[#F5F3EF]">
      <SeoHead
        title={pageData?.seo_title || "Kontakt JARCon ApS | Aftal et Lyt i Lytterummet i Sydals"}
        description={
          pageData?.meta_description ||
          "Kontakt Jens hos JARCon ApS for rådgivning om HiFi, specialbyggede højttalere eller et lyt i vores lytterum i Sydals. Tlf: +45 22 88 45 36."
        }
        ogImage="https://assets.auxodev.dk/assets/d6e713fb-6411-4075-ab6a-5936b7a50d5a/2dee4a-75a790a4e93b4a67a6981ddeae1ed7f4-mv2.jpg"
        canonicalUrl="https://www.jarcon.dk/kontakt"
      />

      {/* Hero */}
      <section className="relative border-b border-white/10 bg-[#0E0E10] py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block text-xs uppercase tracking-widest text-[#C87D46] font-medium mb-4">
            Personlig dialog & lytterum
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#F5F3EF]">
            Kontakt JARCon ApS
          </h1>
          <p className="mt-4 font-serif italic text-lg sm:text-2xl text-[#E5E2DB] max-w-2xl mx-auto">
            Aftal et lyt i lytterummet eller hør nærmere om dine muligheder
          </p>
        </div>
      </section>

      {/* Main Content: Info & Form */}
      <section id="kontaktformular" className="py-16 sm:py-24 border-b border-white/10 scroll-mt-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Left Col: Contact Information */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <span className="text-xs uppercase tracking-widest text-[#C87D46] font-medium block mb-2">
                  Direkte kontakt
                </span>
                <h2 className="font-serif text-3xl sm:text-4xl text-[#F5F3EF]">
                  Lad os tage en snak om god lyd
                </h2>
                <p className="mt-4 text-sm sm:text-base text-[#A8A49C] leading-relaxed">
                  Du er altid velkommen til at kontakte mig for en uforpligtende dialog om højtalere, udmåling med CLIO pocket, forhandling af Hypex elektronik eller for at aftale et besøg i lytterummet.
                </p>
              </div>

              {/* Contact info cards */}
              <div className="space-y-4 text-sm">
                {/* Telefon */}
                <div className="rounded-sm border border-white/10 bg-[#18181C] p-5 flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-[#121214] border border-[#C87D46]/40 text-[#C87D46]">
                    <i className="fa-solid fa-phone text-sm" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-wider text-[#A8A49C] block">
                      Telefon
                    </span>
                    <a
                      href="tel:+4522884536"
                      className="font-serif text-lg text-[#F5F3EF] hover:text-[#C87D46] transition-colors whitespace-nowrap inline-block mt-0.5"
                    >
                      +45 22 88 45 36
                    </a>
                    <p className="text-xs text-[#716E68] mt-1">Ring eller send en sms</p>
                  </div>
                </div>

                {/* E-mail */}
                <div className="rounded-sm border border-white/10 bg-[#18181C] p-5 flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-[#121214] border border-[#C87D46]/40 text-[#C87D46]">
                    <i className="fa-solid fa-envelope text-sm" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-wider text-[#A8A49C] block">
                      E-mail
                    </span>
                    <a
                      href="mailto:jens@jarcon.dk"
                      className="font-serif text-lg text-[#F5F3EF] hover:text-[#C87D46] transition-colors whitespace-nowrap inline-block mt-0.5"
                    >
                      jens@jarcon.dk
                    </a>
                    <p className="text-xs text-[#716E68] mt-1">Vi besvarer snarest muligt</p>
                  </div>
                </div>

                {/* Adresser */}
                <div className="rounded-sm border border-white/10 bg-[#18181C] p-5 space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-[#121214] border border-[#C87D46]/40 text-[#C87D46]">
                      <i className="fa-solid fa-location-dot text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-xs uppercase tracking-wider text-[#C87D46] font-medium block">
                        Adresse / Lytterum
                      </span>
                      <p className="font-serif text-base text-[#F5F3EF] mt-0.5">Toftehøj 21</p>
                      <p className="text-sm text-[#A8A49C]">6470 Sydals</p>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-white/5 pl-14">
                    <span className="text-xs uppercase tracking-wider text-[#C87D46] font-medium block">
                      Produktionsadresse
                    </span>
                    <p className="font-serif text-base text-[#F5F3EF] mt-0.5">Huholt 1A</p>
                    <p className="text-sm text-[#A8A49C]">6400 Sønderborg</p>
                    <p className="text-xs text-[#716E68] mt-2">CVR: 39150255 · JARCon ApS</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Col: Webform */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
